import { useCallback, useEffect, useRef, useState } from 'react';

/** Wait after the last edit before sending. Same delay on every prospect form. */
export const AUTOSAVE_DEBOUNCE_MS = 800;
/** While one save is running, keep only the latest snapshot. */
export const AUTOSAVE_MAX_IN_FLIGHT = 1;
/** One extra attempt when the connection drops. */
export const AUTOSAVE_RETRY_LIMIT = 1;
/** Server logs a prospect save as slow at this duration. */
export const AUTOSAVE_SLOW_MS = 1000;

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function isAutosaveNetworkError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const message = err.message.toLowerCase();
  return message === 'failed to fetch'
    || message.includes('network')
    || message.includes('load failed')
    || message.includes('networkerror');
}

export interface AutosaveFlushOptions {
  keepalive?: boolean;
}

interface UseAutosaveOptions {
  /** JSON snapshot of the fields to persist. Null while there is nothing to save. */
  snapshot: string | null;
  /** Hydration finished. The first ready snapshot is the baseline and is not sent. */
  ready: boolean;
  /** When false, edits wait until this becomes true (for example a required name). */
  enabled: boolean;
  /**
   * Changing this acks the current snapshot without saving.
   * Use it when the user switches person or home.
   */
  resetKey?: string | null;
  /**
   * Snapshot already stored on the server for this resetKey.
   * When it differs from `snapshot`, the hook saves (unsynced local draft).
   */
  baselineSnapshot?: string | null;
  save: (snapshot: string, context: { keepalive: boolean }) => Promise<void>;
  onError?: (err: unknown) => void;
  onSaved?: (snapshot: string) => void;
}

export interface AutosaveControls {
  status: AutosaveStatus;
  error: string | null;
  flush: (options?: AutosaveFlushOptions) => Promise<void>;
  /** Mark a snapshot as already stored so the hook will not send it again. */
  acknowledge: (snapshot: string) => void;
}

export function useAutosave({
  snapshot,
  ready,
  enabled,
  resetKey = null,
  baselineSnapshot = null,
  save,
  onError,
  onSaved,
}: UseAutosaveOptions): AutosaveControls {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const saveRef = useRef(save);
  const onErrorRef = useRef(onError);
  const onSavedRef = useRef(onSaved);
  const snapshotRef = useRef(snapshot);
  const enabledRef = useRef(enabled);
  const readyRef = useRef(ready);
  saveRef.current = save;
  onErrorRef.current = onError;
  onSavedRef.current = onSaved;
  snapshotRef.current = snapshot;
  enabledRef.current = enabled;
  readyRef.current = ready;

  const lastAckedRef = useRef<string | null>(null);
  const failedRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const resetKeyRef = useRef<string | null | undefined>(undefined);
  const runSaveRef = useRef<(target: string, keepalive: boolean) => Promise<void>>(async () => undefined);

  const acknowledge = useCallback((next: string) => {
    lastAckedRef.current = next;
    failedRef.current = null;
    if (pendingRef.current === next) pendingRef.current = null;
  }, []);

  useEffect(() => {
    runSaveRef.current = async (target: string, keepalive: boolean) => {
      if (!enabledRef.current || !readyRef.current) return;
      if (target === lastAckedRef.current) return;
      if (inFlightRef.current && AUTOSAVE_MAX_IN_FLIGHT === 1) {
        pendingRef.current = target;
        return;
      }
      inFlightRef.current = true;
      pendingRef.current = null;
      setStatus('saving');
      setError(null);
      let attempt = 0;
      let lastError: unknown = null;
      while (attempt <= AUTOSAVE_RETRY_LIMIT) {
        try {
          await saveRef.current(target, { keepalive: keepalive && attempt === 0 });
          lastAckedRef.current = target;
          failedRef.current = null;
          lastError = null;
          setStatus('saved');
          onSavedRef.current?.(target);
          break;
        } catch (err) {
          lastError = err;
          const canRetry = isAutosaveNetworkError(err) && attempt < AUTOSAVE_RETRY_LIMIT;
          if (!canRetry) break;
          attempt += 1;
        }
      }
      inFlightRef.current = false;
      if (lastError) {
        failedRef.current = target;
        const message = lastError instanceof Error ? lastError.message : 'Unable to save.';
        setStatus('error');
        setError(message);
        onErrorRef.current?.(lastError);
      }
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (
        pending
        && pending !== lastAckedRef.current
        && enabledRef.current
        && readyRef.current
      ) {
        await runSaveRef.current(pending, false);
      }
    };
  }, []);

  const flush = useCallback(async (options?: AutosaveFlushOptions) => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const target = snapshotRef.current;
    if (!readyRef.current || !enabledRef.current || target == null) return;
    if (target === lastAckedRef.current) return;
    await runSaveRef.current(target, Boolean(options?.keepalive));
  }, []);

  useEffect(() => {
    if (!ready || snapshot == null) return undefined;
    if (resetKeyRef.current !== resetKey) {
      resetKeyRef.current = resetKey;
      lastAckedRef.current = baselineSnapshot ?? snapshot;
      failedRef.current = null;
      if (snapshot === lastAckedRef.current || !enabled) return undefined;
    }
    if (!enabled) return undefined;
    if (snapshot === lastAckedRef.current) return undefined;
    if (snapshot === failedRef.current) return undefined;
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    const target = snapshot;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void runSaveRef.current(target, false);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [baselineSnapshot, enabled, ready, resetKey, snapshot]);

  useEffect(() => {
    const onHide = () => {
      void flush({ keepalive: true });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') onHide();
    };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisibility);
      void flush({ keepalive: true });
    };
  }, [flush]);

  return { status, error, flush, acknowledge };
}
